import { FCM } from "@capacitor-community/fcm";
import { PushNotifications } from "@capacitor/push-notifications";
import { supabase } from "@/integrations/supabase/client";
import { useAuthStore } from "@/store/authStore";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { useChatStore } from "@/store/chatStore";

// Define notification data type
interface NotificationData {
  orderId?: string;
  messageId?: string;
  chatId?: string;
  senderId?: string;
  senderName?: string;
  messageType?: string;
  storeId?: string;
  navigationRoute?: string;
  type?: string;
  [key: string]: string | number | boolean | undefined;
}

class FCMService {
  private currentUser = useAuthStore.getState().user;
  private navigate?: (path: string) => void;
  private isInitialized = false;

  // Set navigation function from the app
  setNavigate(navigate: (path: string) => void) {
    this.navigate = navigate;
  }

  /**
   * Centralized FCM token management
   * This is the ONLY place that should update the FCM token in the database
   */
  private async updateFCMTokenInDatabase(token: string): Promise<boolean> {
    try {
      const currentUser = useAuthStore.getState().user;
      if (!currentUser) {
        console.error("❌ No current user found for FCM token update");
        return false;
      }

      const { error } = await supabase
        .from("users")
        .update({ fcm_token: token })
        .eq("id", currentUser.id);

      if (error) {
        console.error("❌ Error updating FCM token in database:", error);
        return false;
      }
      
      // Update the auth store to keep it in sync
      useAuthStore.getState().updateUser({ fcm_token: token });
      
      return true;
    } catch (error) {
      console.error("❌ Error in updateFCMTokenInDatabase:", error);
      return false;
    }
  }

  /**
   * Get current FCM token from device
   */
  private async getCurrentFCMToken(): Promise<string | null> {
    try {
      const token = await FCM.getToken();
      return token?.token || null;
    } catch (error) {
      console.error("❌ Error getting FCM token:", error);
      return null;
    }
  }

  /**
   * Initialize FCM service
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      // Request permission
      const permission = await PushNotifications.requestPermissions();
      if (permission.receive !== "granted") {
        return;
      }

      // Register for push notifications
      await PushNotifications.register();

      // Set up listeners
      this.setupListeners();

      // Get and update FCM token
      const token = await this.getCurrentFCMToken();
      if (token) {
        await this.updateFCMTokenInDatabase(token);
      }

      this.isInitialized = true;
    } catch (error) {
      console.error("❌ Error initializing FCM:", error);
    }
  }

  /**
   * Refresh FCM token (call this when token changes)
   */
  async refreshToken(): Promise<boolean> {
    try {
      const token = await this.getCurrentFCMToken();
      if (!token) {
        return false;
      }
      return await this.updateFCMTokenInDatabase(token);
    } catch (error) {
      console.error("❌ Error refreshing FCM token:", error);
      return false;
    }
  }

  private setupListeners() {
    // Handle notification received when app is in foreground
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      this.handleNotificationReceived(notification);
    });

    // Handle notification clicked/tapped
    PushNotifications.addListener("pushNotificationActionPerformed", (notification) => {
      this.handleNotificationClicked(notification);
    });
  }

  private async handleNotificationReceived(notification) {
    const data = notification.data as NotificationData;
    
    if (!data) return;

    // Update unread messages count for chat notifications
    if (data.type === 'new_message' || data.type === 'message_reply') {
      const chatStore = useChatStore.getState();
      if (data.storeId) {
        await chatStore.fetchMessages(data.storeId);
      }
    }

    // Show toast notification if app is in foreground
    if (document.visibilityState === "visible") {
      // You can implement your toast notification here
    }
  }

  private async handleNotificationClicked(notification) {
    const data = notification.notification?.data as NotificationData;
    
    if (!data) return;

    // Handle navigation based on notification type
    await this.handleNotificationNavigation(data);
  }

  private async handleNotificationNavigation(data: NotificationData) {
    if (!this.navigate) {
      return;
    }

    // Handle different notification types
    switch (data.type) {
      case 'order_placed':
      case 'order_status_updated':
      case 'order_ready':
      case 'order_completed':
      case 'order_cancelled':
        if (data.orderId) {
          this.navigate(`/dashboard/orders?orderId=${data.orderId}`);
        } else if (data.navigationRoute) {
          this.navigate(data.navigationRoute);
        }
        break;

      case 'new_message':
      case 'message_reply':
        if (data.messageId) {
          this.navigate(`/dashboard/chat?messageId=${data.messageId}`);
        } else if (data.navigationRoute) {
          this.navigate(data.navigationRoute);
        }
        break;

      case 'reservation_created':
      case 'reservation_confirmed':
      case 'reservation_cancelled':
        if (data.reservationId) {
          this.navigate(`/dashboard/reservations?reservationId=${data.reservationId}`);
        } else if (data.navigationRoute) {
          this.navigate(data.navigationRoute);
        }
        break;

      case 'table_assigned':
      case 'table_unassigned':
        if (data.tableId) {
          this.navigate(`/dashboard/tables?tableId=${data.tableId}`);
        } else if (data.navigationRoute) {
          this.navigate(data.navigationRoute);
        }
        break;

      case 'inventory_low_stock':
      case 'inventory_out_of_stock':
      case 'inventory_critical':
        if (data.inventoryItemId) {
          this.navigate(`/dashboard/inventory?itemId=${data.inventoryItemId}`);
        } else if (data.navigationRoute) {
          this.navigate(data.navigationRoute);
        }
        break;

      default:
        // For unknown types, try to use the navigation route if provided
        if (data.navigationRoute) {
          this.navigate(data.navigationRoute);
        } else if (data.storeId) {
          // Default to dashboard if we have a store ID
          this.navigate('/dashboard');
        }
        break;
    }
  }

  // Send a chat notification
  async sendChatNotification(
    recipientId: string,
    messageData: {
      messageId: string;
      content: string;
      senderName: string;
      senderId: string;
      storeId: string;
      messageType: string;
    }
  ): Promise<void> {
    try {
      // Get recipient's FCM token
      const { data: recipient, error: userError } = await supabase
        .from('users')
        .select('fcm_token, push_updates')
        .eq('id', recipientId)
        .single();

      if (userError || !recipient) {
        console.error("❌ Error fetching recipient data:", userError);
        return;
      }

      // Check if user has enabled chat notifications
      if (recipient.push_updates === false) {
        return;
      }

      // Prepare notification payload
      const notificationPayload = {
        token: recipient.fcm_token,
        title: `New message from ${messageData.senderName}`,
        body:
          messageData.messageType === "text"
            ? messageData.content.substring(0, 100)
            : `Sent a ${messageData.messageType}`,
        data: {
          type: "new_message",
          messageId: messageData.messageId,
          senderId: messageData.senderId,
          senderName: messageData.senderName,
          storeId: messageData.storeId,
          messageType: messageData.messageType,
          navigationRoute: `/dashboard/chat?messageId=${messageData.messageId}`,
        },
      };

      // Send notification through Supabase Edge Function
      const { error } = await supabase.functions.invoke("fcm-notify", {
        body: notificationPayload,
      });

      if (error) {
        console.error("❌ Error sending chat notification:", error);
      }
    } catch (error) {
      console.error("❌ Error in sendChatNotification:", error);
    }
  }
}

// Create singleton instance
export const fcmService = new FCMService();
